import { useState } from 'react'
import { Eye, ScrollText } from 'lucide-react'
import { EmptyState, ErrorState, FilterBar, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useAccessLog, useUserPicker } from '../api'
import { Select } from '@/components/ui/select'

/** The record types that carry fields worth logging a read of. */
const ENTITY_TYPES = [
  { value: '', label: 'Anything' },
  { value: 'EMPLOYEE_DOCUMENT', label: 'Document numbers' },
  { value: 'EMPLOYEE_BANK_ACCOUNT', label: 'Bank details' },
  { value: 'PERSON', label: 'Personal identifiers' },
]

/**
 * Who read what, and which fields they were shown.
 *
 * <p>The distinction that makes this worth keeping: it records the fields <em>revealed</em>,
 * not the records opened. A screen that renders every passport number on load would turn this
 * into a log of page views and tell nobody anything — which is why the documents tab hides
 * numbers until somebody presses Reveal, and why that press is what lands here.
 *
 * <p>Read-only, and there is no delete. An audit trail an administrator can edit is not one.
 */
export function AccessLogPage() {
  useBreadcrumb()
  const [entityType, setEntityType] = useState('')
  const [userId, setUserId] = useState('')
  const users = useUserPicker()
  const [page, setPage] = useState(0)
  const size = 50

  const log = useAccessLog({ entityType, userId, page, size })
  const total = log.data?.totalElements ?? 0
  const lastPage = Math.max(0, Math.ceil(total / size) - 1)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sensitive access log"
        subtitle="Every time somebody was shown a document number, a bank detail or an identifier."
      />

      <FilterBar>
        <Select
          aria-label="Kind of record"
          id="log-entity-type"
          value={entityType}
          onChange={(event) => { setEntityType(event.target.value); setPage(0) }}
        >
          {ENTITY_TYPES.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>

        {/* A user picker rather than "paste a user id"; falls back to the id field when the
            caller may not list users. */}
        {users.isError ? (
          <Input
            aria-label="User id"
            id="log-user"
            className="w-80"
            placeholder="Paste a user id to see only their reads"
            value={userId}
            onChange={(event) => { setUserId(event.target.value); setPage(0) }}
          />
        ) : (
          <Select
            aria-label="User"
            id="log-user"
            className="w-80"
            value={userId}
            onChange={(event) => { setUserId(event.target.value); setPage(0) }}
          >
            <option value="">All users</option>
            {users.data?.map((user) => (
              <option key={user.id} value={user.id}>{user.fullName} — {user.email}</option>
            ))}
          </Select>
        )}
      </FilterBar>

      <Card>
        {log.isPending && <Skeleton className="m-4 h-24" />}
        {log.isError && <ErrorState error={log.error} onRetry={() => void log.refetch()} />}
        {log.isSuccess && log.data.content.length === 0 && (
          <EmptyState
            title="Nothing has been revealed"
            description="Numbers stay hidden until somebody asks to see one. An empty log means nobody has."
          />
        )}
        {log.isSuccess && log.data.content.length > 0 && (
          <>
            <div className="overflow-x-auto overscroll-x-contain">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="p-3 text-start font-medium">When</th>
                    <th className="p-3 text-start font-medium">Who</th>
                    <th className="p-3 text-start font-medium">Record</th>
                    <th className="p-3 text-start font-medium">Fields shown</th>
                    <th className="p-3 text-start font-medium">Reason given</th>
                    <th className="p-3 text-start font-medium">From</th>
                  </tr>
                </thead>
                <tbody>
                  {log.data.content.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="p-3 whitespace-nowrap">{row.accessedAt.replace('T', ' ').slice(0, 19)}</td>
                      <td className="p-3">{row.userEmail ?? row.userId}</td>
                      <td className="p-3 text-slate-600">
                        {row.entityType}
                        <span className="block font-mono text-xs">{row.entityId.slice(0, 8)}…</span>
                      </td>
                      <td className="p-3">
                        <span className="flex flex-wrap gap-1">
                          {row.fields.map((field) => (
                            <code key={field} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                              {field}
                            </code>
                          ))}
                        </span>
                      </td>
                      <td className="p-3 text-slate-600">{row.reason ?? '—'}</td>
                      <td className="p-3 font-mono text-xs text-slate-600">{row.ipAddress ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 p-3 text-sm">
              <span className="text-slate-600">
                {total} read{total === 1 ? '' : 's'} recorded
              </span>
              <span className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  Previous
                </Button>
                <span className="text-slate-600">page {page + 1} of {lastPage + 1}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= lastPage}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </span>
            </div>
          </>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Eye className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Rows are written when a value is <strong className="mx-1">revealed</strong>, not when a
        record is opened. There is no way to remove one from here — an audit trail an
        administrator can edit is not an audit trail.
        <ScrollText className="sr-only" aria-hidden="true" />
      </p>
    </div>
  )
}
